import React, { useState, useEffect } from 'react';
import RispNavBar from '../components/RISP/RispNavBar';
import RispPageShell from '../components/RISP/RispPageShell';
import { apiService } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useRispWorkingCountry } from '../hooks/useRispWorkingCountry';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { diseaseOptions } from '../services/risp/rispService';

function countriesMatch(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const na = a.normalize('NFC').toLowerCase().trim();
  const nb = b.normalize('NFC').toLowerCase().trim();
  if (na === nb) return true;
  const turk = (s: string) => s.includes('turk') || s.includes('türk') || s.includes('tuerk');
  return turk(na) && turk(nb);
}

function parseSummaryPeriod(key: string): { year: number; quarter: number } | null {
  const match = key.match(/-(\d{4})-([1-4])-/);
  if (!match) return null;
  return { year: parseInt(match[1], 10), quarter: parseInt(match[2], 10) };
}

// Comprehensive safe parsing function
const safeParseData = (data: any, fallback: any = []): any => {
  if (!data) return fallback;
  
  // If it's already an array or object, return it
  if (Array.isArray(data) || (typeof data === 'object' && data !== null)) {
    return data;
  }
  
  // If it's a string, try different parsing approaches
  if (typeof data === 'string') {
    // If it's already comma-separated values, split them
    if (data.includes(',') && !data.trim().startsWith('[') && !data.trim().startsWith('{')) {
      return data.split(',').map(item => item.trim()).filter(item => item.length > 0);
    }
    
    // Try JSON parsing
    try {
      const parsed = JSON.parse(data);
      return parsed;
    } catch {
      // If JSON parsing fails, return as single item array or the fallback
      return data.trim() ? [data.trim()] : fallback;
    }
  }
  
  return fallback;
};

// Function to normalize disease names to avoid duplicates
const normalizeDiseaseNames = (diseaseNamesList: any[]) => {
  return diseaseNamesList.map(item => {
    let normalizedName = item.disease_name;
    
    // Normalize FMD variations
    if (normalizedName) {
      if (normalizedName.toLowerCase().includes('foot and mouth') || 
          normalizedName.toLowerCase() === 'fmd' ||
          normalizedName.toLowerCase().includes('foot-and-mouth')) {
        normalizedName = 'Foot and Mouth Disease';
      }
    }
    
    return {
      ...item,
      disease_name: normalizedName
    };
  });
};

// Function to merge duplicate diseases (same normalized name)
const mergeDuplicateDiseases = (data: any[]) => {
  const diseaseMap = new Map();
  
  data.forEach(item => {
    const key = item.disease_name;
    if (diseaseMap.has(key)) {
      // Merge data - keep first entry but combine any relevant fields
      const existing = diseaseMap.get(key);
      // For surveillance, we can merge details if they're different
      if (item.details && existing.details && item.details !== existing.details) {
        existing.details = `${existing.details}; ${item.details}`;
      }
    } else {
      diseaseMap.set(key, { ...item });
    }
  });
  
  return Array.from(diseaseMap.values());
};

// Format array data for display
const formatArrayData = (data: any): string => {
  const parsed = safeParseData(data, []);
  if (Array.isArray(parsed)) {
    return parsed.join(', ') || 'N/A';
  }
  return String(parsed) || 'N/A';
};

// Utility to get full display name from diseaseOptions
const getDiseaseDisplayName = (diseaseName: string): string => {
  const found = diseaseOptions.find(opt =>
    diseaseName && (diseaseName.toLowerCase().includes(opt.name.split(' - ')[1]?.toLowerCase() || '') ||
    opt.name.toLowerCase().includes(diseaseName.toLowerCase()))
  );
  return found ? found.name : diseaseName;
};

const RISPSummary: React.FC = () => {
  const { user } = useAuthStore();
  const {
    isAdmin,
    workingCountry,
    adminCountry,
    setAdminCountry,
    northAfricaCountries,
  } = useRispWorkingCountry();

  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const [currentQuarter, setCurrentQuarter] = useState<string>('');
  const [outbreakData, setOutbreakData] = useState<any[]>([]);
  const [surveillanceData, setSurveillanceData] = useState<any[]>([]);
  const [vaccinationData, setVaccinationData] = useState<any[]>([]);
  const [marketPriceData, setMarketPriceData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadSummaryData = async () => {
      if (!workingCountry) {
        setOutbreakData([]);
        setSurveillanceData([]);
        setVaccinationData([]);
        setMarketPriceData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Resolve latest quarter that has RISP data for this country (not calendar guess)
        let year: number | null = null;
        let quarterNum: number | null = null;

        try {
          const dashRes = await fetch('/api/risp/dashboard');
          if (dashRes.ok) {
            const dash = await dashRes.json();
            const periodSet = new Set<string>();
            for (const row of dash.data || []) {
              if (
                countriesMatch(row.Country, workingCountry) &&
                row.Year &&
                row.Quarter
              ) {
                periodSet.add(`${row.Year}-${row.Quarter}`);
              }
            }
            for (const key of Object.keys(dash.surveillance_summary || {})) {
              if (!key.toLowerCase().startsWith(workingCountry.toLowerCase().slice(0, 3))) {
                // cheap prefilter; precise match below
              }
              const p = parseSummaryPeriod(key);
              if (!p) continue;
              const countryFromKey = key.slice(
                0,
                key.lastIndexOf(`-${p.year}-${p.quarter}-`)
              );
              if (countriesMatch(countryFromKey, workingCountry)) {
                periodSet.add(`${p.year}-${p.quarter}`);
              }
            }
            for (const key of Object.keys(dash.vaccination_summary || {})) {
              const p = parseSummaryPeriod(key);
              if (!p) continue;
              const countryFromKey = key.slice(
                0,
                key.lastIndexOf(`-${p.year}-${p.quarter}-`)
              );
              if (countriesMatch(countryFromKey, workingCountry)) {
                periodSet.add(`${p.year}-${p.quarter}`);
              }
            }

            const ranked = Array.from(periodSet)
              .map((k) => {
                const [y, q] = k.split('-').map(Number);
                return { year: y, quarter: q };
              })
              .sort((a, b) => b.year - a.year || b.quarter - a.quarter);

            if (ranked.length) {
              year = ranked[0].year;
              quarterNum = ranked[0].quarter;
            } else if (dash.latest_period?.year && dash.latest_period?.quarter) {
              year = Number(dash.latest_period.year);
              quarterNum = Number(dash.latest_period.quarter);
            }
          }
        } catch {
          /* fall through */
        }

        if (year == null || quarterNum == null) {
          const month = new Date().getMonth() + 1;
          const cq = Math.ceil(month / 3);
          const cy = new Date().getFullYear();
          if (cq === 1) {
            year = cy - 1;
            quarterNum = 4;
          } else {
            year = cy;
            quarterNum = cq - 1;
          }
        }

        const quarterLabel = `Q${quarterNum}`;
        setCurrentYear(year);
        setCurrentQuarter(quarterLabel);

        const outbreakResponse = await apiService.risp.getOutbreaks(
          year,
          quarterLabel,
          workingCountry
        );
        const parsedOutbreaks = (outbreakResponse.data || []).map((item: any) => ({
          ...item,
          locations: safeParseData(item.locations, []),
          status: safeParseData(item.status, []),
          serotype: safeParseData(item.serotype, []),
          species: safeParseData(item.species, []),
          control_measures: safeParseData(item.control_measures, []),
        }));

        const surveillanceResponse = await apiService.risp.getRISP({
          type: 'surveillance',
          year: String(year),
          quarter: quarterLabel,
          country: workingCountry,
        });
        const parsedSurveillance = (surveillanceResponse.data || []).map((item: any) => ({
          ...item,
          active_surveillance: safeParseData(item.active_surveillance, []),
        }));
        const mergedSurveillance = mergeDuplicateDiseases(
          normalizeDiseaseNames(parsedSurveillance)
        );

        const vaccinationResponse = await apiService.risp.getRISPVaccinations(
          String(year),
          workingCountry
        );
        const campaignsWithCurrentData = (vaccinationResponse.data || []).filter(
          (campaign: any) => {
            const quarterField = `q${quarterNum}`;
            return campaign[quarterField] && Number(campaign[quarterField]) > 0;
          }
        );

        let marketRows: any[] = [];
        try {
          const marketResponse = await apiService.risp.getMarketPrices(
            String(year),
            quarterLabel,
            workingCountry
          );
          marketRows = Array.isArray(marketResponse.data) ? marketResponse.data : [];
        } catch {
          marketRows = [];
        }

        setOutbreakData(parsedOutbreaks);
        setSurveillanceData(mergedSurveillance);
        setVaccinationData(campaignsWithCurrentData);
        setMarketPriceData(marketRows);
      } catch (error) {
        console.error('Error loading summary data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSummaryData();
  }, [workingCountry]);

  const quarterKey = currentQuarter ? currentQuarter.toLowerCase() : '';

  // PDF generation logic
  const handlePrint = () => {
    const doc = new jsPDF('p', 'pt');
    doc.setFont('Helvetica', 'normal');
    
    let yPosition = 40;
    
    // Title
    doc.setFontSize(22);
    doc.text('RISP Summary Report', 20, yPosition);
    yPosition += 30;
    
    // Country and Period
    doc.setFontSize(12);
    doc.text(`Country: ${workingCountry || user?.country || ''}`, 20, yPosition);
    yPosition += 15;
    doc.text(`Period: ${currentQuarter} ${currentYear ?? ''} (latest available)`, 20, yPosition);
    yPosition += 20;
    
    // Outbreak Data Table
    if (outbreakData.length > 0) {
      doc.setFontSize(16);
      doc.text('Disease Outbreaks', 20, yPosition);
      yPosition += 10;
      
      const outbreakRows = outbreakData.map(outbreak => [
        getDiseaseDisplayName(outbreak.disease_name),
        outbreak.number_outbreaks,
        formatArrayData(outbreak.species),
        formatArrayData(outbreak.status),
        formatArrayData(outbreak.locations)
      ]);
      
      autoTable(doc, {
        head: [['Disease', 'Number of Outbreaks', 'Species', 'Status', 'Locations']],
        body: outbreakRows,
        startY: yPosition,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [21, 115, 109] }
      });
      
      yPosition = (doc as any).lastAutoTable.finalY + 20;
    }
    
    // Surveillance Data Table
    if (surveillanceData.length > 0) {
      doc.setFontSize(16);
      doc.text('Surveillance Data', 20, yPosition);
      yPosition += 10;
      
      const surveillanceRows = surveillanceData.map(surveillance => [
        getDiseaseDisplayName(surveillance.disease_name),
        surveillance.passive_surveillance ? 'Yes' : 'No',
        formatActiveSurveillance(surveillance.active_surveillance),
        surveillance.details || 'N/A'
      ]);
      
      autoTable(doc, {
        head: [['Disease', 'Passive Surveillance', 'Active Surveillance', 'Details']],
        body: surveillanceRows,
        startY: yPosition,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [21, 115, 109] }
      });
      
      yPosition = (doc as any).lastAutoTable.finalY + 20;
    }
    
    // Vaccination Data Table
    if (vaccinationData.length > 0) {
      doc.setFontSize(16);
      doc.text('Vaccination Campaigns', 20, yPosition);
      yPosition += 10;
      
      const vaccinationRows = vaccinationData.map(vaccination => [
        getDiseaseDisplayName(vaccination.disease_name) || 'N/A',
        vaccination.vaccination_type || vaccination.strategy || 'N/A',
        formatArrayData(vaccination.species),
        formatArrayData(vaccination.geographical_areas),
        String(vaccination[quarterKey] || 0)
      ]);
      
      autoTable(doc, {
        head: [['Disease', 'Vaccination Type', 'Species', 'Geographical Areas', `Animals Vaccinated (${currentQuarter})`]],
        body: vaccinationRows,
        startY: yPosition,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [21, 115, 109] }
      });
      yPosition = (doc as any).lastAutoTable.finalY + 20;
    }

    if (marketPriceData.length > 0) {
      doc.setFontSize(16);
      doc.text('Market Prices', 20, yPosition);
      yPosition += 10;

      const marketRows = marketPriceData.map((row) => [
        row.species || 'N/A',
        row.market_level || 'N/A',
        row.product || 'N/A',
        row.price_min != null ? String(row.price_min) : '—',
        row.price_max != null ? String(row.price_max) : '—',
        row.price_avg != null ? String(row.price_avg) : '—',
        row.reference || '—',
      ]);

      autoTable(doc, {
        head: [['Species', 'Market level', 'Product', 'Min', 'Max', 'Average', 'Reference']],
        body: marketRows,
        startY: yPosition,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [21, 115, 109] },
      });
    }
    
    // Save the PDF
    doc.save(
      `RISP_Summary_${currentQuarter}_${currentYear}_${workingCountry || user?.country || 'Report'}.pdf`
    );
  };

  const formatActiveSurveillance = (activeSurveillanceData: any) => {
    const parsed = safeParseData(activeSurveillanceData, []);
    if (Array.isArray(parsed)) {
      return parsed.join(', ') || 'None';
    }
    return String(parsed) || 'None';
  };

  if (loading) {
    return (
      <RispPageShell>
        <RispNavBar />
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading summary data...</div>
        </div>
      </RispPageShell>
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            .print-page { page-break-after: always; }
            body { -webkit-print-color-adjust: exact; }
          }
        `}
      </style>
      <RispPageShell>
        <div className="no-print">
          <RispNavBar />
        </div>
        
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-center mb-2">RISP Summary Report</h1>
          {isAdmin && (
            <div className="flex justify-center items-center gap-2 mb-4 no-print">
              <label htmlFor="summary-country" className="text-sm font-medium text-gray-700">
                Country
              </label>
              <select
                id="summary-country"
                className="min-w-[12rem] py-2 px-3 border border-gray-300 bg-white rounded-md text-sm"
                value={adminCountry}
                onChange={(e) => setAdminCountry(e.target.value)}
              >
                <option value="">Select country</option>
                {northAfricaCountries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}
          <p className="text-center text-gray-600 mb-1">
            Country: <strong>{workingCountry || '—'}</strong>
          </p>
          <p className="text-center text-gray-600">
            Period:{' '}
            <strong>
              {currentQuarter && currentYear != null
                ? `${currentQuarter} ${currentYear}`
                : '—'}
            </strong>
            <span className="text-gray-500 text-sm"> (latest quarter with RISP data)</span>
          </p>
          {!workingCountry && (
            <p className="text-center text-amber-700 text-sm mt-2">
              {isAdmin
                ? 'Select a country above to load the summary.'
                : 'Your account has no country assigned.'}
            </p>
          )}
        </div>

        {/* Outbreak Data Table */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#15736d' }}>
            Disease Outbreaks
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border border-white bg-white tracking-wider text-sm">
              <thead>
                <tr className="text-white text-sm font-medium" style={{ backgroundColor: '#15736d' }}>
                  <th className="py-2 px-4 border border-white text-left">Disease</th>
                  <th className="py-2 px-4 border border-white text-left">Number of Outbreaks</th>
                  <th className="py-2 px-4 border border-white text-left">Species</th>
                  <th className="py-2 px-4 border border-white text-left">Status</th>
                  <th className="py-2 px-4 border border-white text-left">Locations</th>
                </tr>
              </thead>
              <tbody>
                {outbreakData.length > 0 ? (
                  outbreakData.map((outbreak, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border border-gray-300">{getDiseaseDisplayName(outbreak.disease_name)}</td>
                      <td className="py-2 px-4 border border-gray-300">{outbreak.number_outbreaks}</td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatArrayData(outbreak.species)}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatArrayData(outbreak.status)}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatArrayData(outbreak.locations)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-4 px-4 text-center text-gray-500">
                      No outbreak data for {currentQuarter} {currentYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Surveillance Data Table */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#15736d' }}>
            Surveillance Activities
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border border-white bg-white tracking-wider text-sm">
              <thead>
                <tr className="text-white text-sm font-medium" style={{ backgroundColor: '#15736d' }}>
                  <th className="py-2 px-4 border border-white text-left">Disease</th>
                  <th className="py-2 px-4 border border-white text-left">Passive Surveillance</th>
                  <th className="py-2 px-4 border border-white text-left">Active Surveillance</th>
                  <th className="py-2 px-4 border border-white text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {surveillanceData.length > 0 ? (
                  surveillanceData.map((surveillance, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border border-gray-300">{getDiseaseDisplayName(surveillance.disease_name)}</td>
                      <td className="py-2 px-4 border border-gray-300">
                        {Number(surveillance.passive_surveillance) === 1 ? 'Yes' : 'No'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatActiveSurveillance(surveillance.active_surveillance)}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {surveillance.details || surveillance.additional_info || 'N/A'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-4 px-4 text-center text-gray-500">
                      No surveillance data for {currentQuarter} {currentYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Vaccination Data Table */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#15736d' }}>
            Vaccination Campaigns
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border border-white bg-white tracking-wider text-sm">
              <thead>
                <tr className="text-white text-sm font-medium" style={{ backgroundColor: '#15736d' }}>
                  <th className="py-2 px-4 border border-white text-left">Disease</th>
                  <th className="py-2 px-4 border border-white text-left">Vaccination Type</th>
                  <th className="py-2 px-4 border border-white text-left">Species</th>
                  <th className="py-2 px-4 border border-white text-left">Geographical Areas</th>
                  <th className="py-2 px-4 border border-white text-left">Animals Vaccinated ({currentQuarter})</th>
                </tr>
              </thead>
              <tbody>
                {vaccinationData.length > 0 ? (
                  vaccinationData.map((vaccination, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border border-gray-300">{getDiseaseDisplayName(vaccination.disease_name) || 'N/A'}</td>
                      <td className="py-2 px-4 border border-gray-300">
                        {vaccination.vaccination_type || vaccination.strategy || 'N/A'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatArrayData(vaccination.species)}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {formatArrayData(vaccination.geographical_areas)}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {vaccination[quarterKey] || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-4 px-4 text-center text-gray-500">
                      No vaccination data for {currentQuarter} {currentYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Market prices — only when data exists for this period */}
        {marketPriceData.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#15736d' }}>
              Market Prices
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border border-white bg-white tracking-wider text-sm">
                <thead>
                  <tr className="text-white text-sm font-medium" style={{ backgroundColor: '#15736d' }}>
                    <th className="py-2 px-4 border border-white text-left">Species</th>
                    <th className="py-2 px-4 border border-white text-left">Market level</th>
                    <th className="py-2 px-4 border border-white text-left">Product</th>
                    <th className="py-2 px-4 border border-white text-left">Min</th>
                    <th className="py-2 px-4 border border-white text-left">Max</th>
                    <th className="py-2 px-4 border border-white text-left">Average</th>
                    <th className="py-2 px-4 border border-white text-left">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {marketPriceData.map((row, index) => (
                    <tr key={row.id ?? index} className="hover:bg-gray-50">
                      <td className="py-2 px-4 border border-gray-300 capitalize">{row.species || '—'}</td>
                      <td className="py-2 px-4 border border-gray-300 capitalize">
                        {row.market_level || '—'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300 capitalize">{row.product || '—'}</td>
                      <td className="py-2 px-4 border border-gray-300">
                        {row.price_min != null ? row.price_min : '—'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {row.price_max != null ? row.price_max : '—'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">
                        {row.price_avg != null ? row.price_avg : '—'}
                      </td>
                      <td className="py-2 px-4 border border-gray-300">{row.reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Print Button */}
        <div className="flex justify-center mb-8 no-print">
          <button
            onClick={handlePrint}
            className="px-6 py-3 text-white rounded-lg font-medium"
            style={{ backgroundColor: '#15736d' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0f5b57')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#15736d')}
          >
            📄 Print / Download PDF
          </button>
        </div>
      </RispPageShell>
    </>
  );
};

export default RISPSummary;
